import { supportService } from "@api/support";
import useActiveGym from "@ui/hooks/active-gym";
import { zodResolver } from "@hookform/resolvers/zod";
import { ExternalLink } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { ApiError, handleApiError } from "@/api/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import * as analytics from "@/lib/analytics";

const REQUEST_TYPES = [
  { id: "support", name: "Support" },
  { id: "bug", name: "Bug Report" },
  { id: "feature", name: "Feature Request" },
];

const HELP_CENTER_URL = "https://sendhelios.com/help-center";

const formSchema = z.object({
  type: z.string().min(1, "Required"),
  message: z.string().trim().min(1, "Required"),
});

type HelpSupportFormValues = z.infer<typeof formSchema>;

export interface HelpSupportModalProps {
  open: boolean;
  setOpen: (val: boolean) => void;
}

export function HelpSupportModal(props: HelpSupportModalProps) {
  const { gym } = useActiveGym();
  const form = useForm<HelpSupportFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { type: "support", message: "" },
  });

  async function onSubmit(values: HelpSupportFormValues) {
    if (!gym) return;
    try {
      await supportService.createRequest({
        gym: gym.id,
        type: values.type,
        message: values.message,
      });
      analytics.track("Support Request Created", { gym: gym.name, type: values.type });
      toast.success("Support request sent");
      form.reset();
      props.setOpen(false);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err);
      }
      toast.error("Failed to send support request");
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Help &amp; Support</DialogTitle>
          <DialogDescription>
            Send us a question, bug report, or feature request and we&apos;ll get back to you.
          </DialogDescription>
        </DialogHeader>
        <a
          href={HELP_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 dark:text-brand-accent dark:hover:text-brand-accent/80"
        >
          <ExternalLink className="h-4 w-4" />
          Visit Help Center
        </a>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Request Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a request type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {REQUEST_TYPES.map((requestType) => (
                        <SelectItem key={requestType.id} value={requestType.id}>
                          {requestType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How can we help?</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={5}
                      placeholder="Please describe your question or issue in detail..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => props.setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
